import { parseApiResponseData } from '../apis/Utilities/parseApiResponseData';

const DEFAULT_CONTACT_TITLE = 'Customer Service Rep';
const DEFAULT_CONTACT_LABEL = 'CSR';

/**
 * Extracts a contacts array from supported API response shapes.
 * Supports:
 * 1) { data: 'json-string' }
 * 2) { data: { data: [...] } }
 * 3) direct array payloads
 */
export const parseContactsPayload = (response) => {
	const payload = parseApiResponseData(response, {});

	if (Array.isArray(payload?.data)) {
		return payload.data;
	}

	if (Array.isArray(payload)) {
		return payload;
	}

	return [];
};

/**
 * Groups raw contacts by plant and returns UI-ready structures:
 * - plantOptions: sorted plant names for dropdowns
 * - contactsByPlant: sorted contacts keyed by plant
 */
export const buildContactsByPlant = (contacts = []) => {
	const nextContactsByPlant = {};

	contacts.forEach((contact) => {
		const plantName = contact?.PlantName?.trim();
		if (!plantName) {
			return;
		}

		if (!nextContactsByPlant[plantName]) {
			nextContactsByPlant[plantName] = new Map();
		}

		const email = contact?.Email?.trim() || '';
		const firstName = contact?.FirstName?.trim() || '';
		const lastName = contact?.LastName?.trim() || '';
		const title = contact?.Title?.trim() || DEFAULT_CONTACT_TITLE;
		const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
		const labelBase =
			fullName || email || contact?.UserID || DEFAULT_CONTACT_LABEL;

		nextContactsByPlant[plantName].set(email || labelBase, {
			email,
			fullName,
			title,
			label: `${labelBase} (${title})`,
		});
	});

	const plantOptions = Object.keys(nextContactsByPlant).sort((a, b) =>
		a.localeCompare(b)
	);

	const contactsByPlant = plantOptions.reduce((acc, plantName) => {
		acc[plantName] = Array.from(nextContactsByPlant[plantName].values()).sort(
			(a, b) => a.label.localeCompare(b.label)
		);
		return acc;
	}, {});

	return {
		plantOptions,
		contactsByPlant,
	};
};

/**
 * Convenience helper that parses and normalizes a contacts API response.
 */
export const normalizeContactsResponse = (response) =>
	buildContactsByPlant(parseContactsPayload(response));
